import adminApiClient from "@/lib/api/admin-api-client";

export type AdminMetricsDailyCount = {
  day: string;
  count: number;
};

export type AdminMetricsRange = {
  from: string;
  to: string;
};

export type AdminMetricsSummary = {
  registered_users: number;
  access_requests: number;
  emails_sent: number;
  avg_time_to_complete_seconds: number | null;
  api_keys_approved: number;
  denied: number;
};

export type AdminMetricsTimeSeries = {
  registrations_by_day: AdminMetricsDailyCount[];
  requests_by_day: AdminMetricsDailyCount[];
  emails_by_day: AdminMetricsDailyCount[];
};

export type AdminMetricsResponse = {
  range: AdminMetricsRange;
  summary: AdminMetricsSummary;
  time_series: AdminMetricsTimeSeries;
};

export type AdminMetricsParams = AdminMetricsRange;

const ADMIN_METRICS_PATH = "/api/v1/admin/metrics";

export async function getAdminMetrics(
  params?: AdminMetricsParams,
): Promise<AdminMetricsResponse> {
  const { data } = await adminApiClient.get<AdminMetricsResponse>(
    ADMIN_METRICS_PATH,
    {
      params: params
        ? {
            from: params.from,
            to: params.to,
          }
        : undefined,
    },
  );
  return data;
}

export function formatDurationSeconds(seconds: number | null): string {
  if (seconds == null || Number.isNaN(seconds)) {
    return "—";
  }
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  }
  if (seconds < 3600) {
    return `${Math.round(seconds / 60)}m`;
  }
  if (seconds < 86400) {
    return `${(seconds / 3600).toFixed(1)}h`;
  }
  return `${(seconds / 86400).toFixed(1)}d`;
}

export function mergeDailySeries(
  ...series: AdminMetricsDailyCount[][]
): { day: string; registrations: number; requests: number; emails: number }[] {
  const byDay = new Map<
    string,
    { day: string; registrations: number; requests: number; emails: number }
  >();

  const ensure = (day: string) => {
    const existing = byDay.get(day);
    if (existing) {
      return existing;
    }
    const created = { day, registrations: 0, requests: 0, emails: 0 };
    byDay.set(day, created);
    return created;
  };

  for (const point of series[0] ?? []) {
    ensure(point.day).registrations = point.count;
  }
  for (const point of series[1] ?? []) {
    ensure(point.day).requests = point.count;
  }
  for (const point of series[2] ?? []) {
    ensure(point.day).emails = point.count;
  }

  return [...byDay.values()].sort((left, right) =>
    left.day.localeCompare(right.day),
  );
}
